import { GeoPoint, destinationPoint } from './geo.util';

/**
 * Shared building blocks for NEX-1534's mocked AxisLink location feeds (the Monitored Subject's,
 * served via `PerpetratorLocationsMockInterceptor`, and the Protected Party's wander, applied
 * client-side in `ThreatPairService`). Kept separate from any one mock so both can synthesize a
 * `last-locations`-shaped response — `{ count, rows: [{ gps, isEmergencyState, recordedAt }] }` —
 * the same way.
 */

/**
 * Arbitrary fallback centre used only when no real reference position is available at all (e.g.
 * the Protected Party's own last-locations call hasn't resolved yet), so a mock still returns
 * something plottable.
 */
export const MOCK_FALLBACK_CENTER: GeoPoint = { lat: 39.7392, lng: -104.9903 };

/**
 * How many past samples a mock synthesizes, so the map has a visible movement trail (like the
 * client's `nightweb-pair-monitor` reference widget) instead of a single dot.
 */
export const MOCK_TRAIL_SAMPLE_COUNT = 9;

/**
 * Total time span (ms) a trail's samples cover, working backwards from now. Deliberately less
 * than a full movement cycle (an orbit period, a wander period) — spanning exactly one cycle
 * would land the oldest and newest samples on the same point, closing the trail into a loop with
 * no visible start or end.
 */
export const MOCK_TRAIL_SPAN_MS = 80000;

/** Small per-sample GPS-like noise layered on top of every mock position — see `applyGpsJitter`. */
const GPS_JITTER_METERS = 4;

/**
 * Deterministic pseudo-random value in [0, 1) from a numeric seed (the classic sine-hash trick).
 * Not cryptographic — just enough irregularity that mock GPS paths don't look hand-drawn.
 */
export function pseudoRandomUnit(seed: number): number {
  const x = Math.sin(seed) * 43758.5453123;
  return x - Math.floor(x);
}

/**
 * Nudges `point` by a few meters in a deterministic-but-irregular direction, so a mock path reads
 * as real (slightly imprecise) device data rather than a perfect mathematical curve. Deterministic
 * per timestamp, so re-deriving the same historical instant on a later poll always lands on the
 * same jittered point instead of a trail visibly reshuffling.
 */
export function applyGpsJitter(point: GeoPoint, atMs: number, salt: number): GeoPoint {
  const seed = atMs / 1000 + salt;
  const distance = GPS_JITTER_METERS * pseudoRandomUnit(seed);
  const bearing = pseudoRandomUnit(seed + 91.7) * 360;
  return destinationPoint(point, distance, bearing);
}

/** `MOCK_TRAIL_SAMPLE_COUNT` timestamps spanning `MOCK_TRAIL_SPAN_MS`, oldest first, ending at `now`. */
export function buildMockTrailSampleTimes(now: number): number[] {
  const sampleGapMs = MOCK_TRAIL_SPAN_MS / (MOCK_TRAIL_SAMPLE_COUNT - 1);
  return Array.from(
    { length: MOCK_TRAIL_SAMPLE_COUNT },
    (_, index) => now - (MOCK_TRAIL_SAMPLE_COUNT - 1 - index) * sampleGapMs,
  );
}

/** One `last-locations` row for a mocked position. */
export function toLocationRow(point: GeoPoint, atMs: number, emergency: boolean) {
  return {
    gps: { latitude: String(point.lat), longitude: String(point.lng) },
    isEmergencyState: emergency,
    recordedAt: new Date(atMs).toISOString(),
  };
}
